/**
 * Raw-text numeric parsing for calculator inputs.
 *
 * Calculator fields keep their raw editable string in state and only feed
 * validated numbers into the arithmetic. This keeps three states distinct:
 *
 * - blank (`""`) — the reader cleared the field; editable, never coerced to 0;
 * - malformed/out-of-range (`"abc"`, `"-5"`, `"Infinity"`) — editable, with a
 *   localized error and the result panel suppressed;
 * - a legitimate entered zero (`"0"`) — valid, and the results still render.
 *
 * `Number("")` is `0`, which is exactly the coercion defect this replaces:
 * clearing a field used to silently become a plausible zero.
 */

export interface NumericDomain {
  /** Inclusive lower bound. */
  min: number;
  /** Inclusive upper bound (`Infinity` for unbounded). */
  max: number;
  /** When true, fractional values are rejected. */
  integer: boolean;
}

/** Any nonnegative finite number (salary, overhead, revenue, ...). */
export const NONNEGATIVE_NUMBER: NumericDomain = {
  min: 0,
  max: Infinity,
  integer: false,
};

/** A 0–100 work-contribution percentage. */
export const PERCENT_0_TO_100: NumericDomain = {
  min: 0,
  max: 100,
  integer: false,
};

export type NumericParseError =
  | "blank"
  | "not-a-number"
  | "below-minimum"
  | "above-maximum"
  | "not-integer";

export type ParsedNumericInput =
  | { ok: true; value: number }
  | { ok: false; error: NumericParseError };

/**
 * Parse raw input text against a nonnegative-style domain. Blank is reported
 * (not coerced); nonfinite text such as `"Infinity"` is `not-a-number`;
 * negatives, over-maximum values and fractions (for integer domains) each get
 * their own reason so callers can point at the offending field.
 */
export function parseNumericInput(
  raw: string,
  domain: NumericDomain,
): ParsedNumericInput {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return { ok: false, error: "blank" };
  }
  const value = Number(trimmed);
  if (!Number.isFinite(value)) {
    return { ok: false, error: "not-a-number" };
  }
  if (value < domain.min) {
    return { ok: false, error: "below-minimum" };
  }
  if (value > domain.max) {
    return { ok: false, error: "above-maximum" };
  }
  if (domain.integer && !Number.isInteger(value)) {
    return { ok: false, error: "not-integer" };
  }
  return { ok: true, value };
}
